import { api } from './api.ts';
import { cipher } from './session.ts';
import type {
  EventDetail,
  EventNotePage,
  EventProgress,
  EventShareStatus,
  EventSummary,
} from '@/types/event.ts';
import type { DaoApiPagination, DaoApiResponse } from '@/types/dao.ts';

/**
 * 이벤트 API.
 *
 * 백엔드 `ControllerApiEvent`. 로그인이 필요 없는 읽기 전용 콘텐츠지만 다른 API 와
 * 같은 규약으로 본문을 AES-GCM 으로 감싸 보낸다.
 */

/** 목록 */
export async function fetchEvents(
  page = 1
): Promise<DaoApiResponse<DaoApiPagination<EventSummary>>> {
  const encrypted = await cipher.encryptJson({ p: page });
  return api.post<DaoApiResponse<DaoApiPagination<EventSummary>>>(
    '/api/event/items.json',
    encrypted
  );
}

/** 상세 (감상평 첫 장 + 내 참여 현황 포함) */
export async function fetchEvent(slug: string, userSeq = 0): Promise<DaoApiResponse<EventDetail>> {
  const encrypted = await cipher.encryptJson({ slug, userSeq });
  return api.post<DaoApiResponse<EventDetail>>('/api/event/data.json', encrypted);
}

/** 감상평 다음 장 */
export async function fetchEventNotes(
  slug: string,
  page: number
): Promise<DaoApiResponse<EventNotePage>> {
  const encrypted = await cipher.encryptJson({ slug, page });
  return api.post<DaoApiResponse<EventNotePage>>('/api/event/notes.json', encrypted);
}

////////////////////////////////////////////////////////////////////////////////
// 참여 (응모권)
//
// 조회는 로그인이 없어도 되지만 참여는 회원만 받는다. 경품을 보낼 곳이 있어야
// 하기 때문이다. 어느 요청이든 응답으로 **갱신된 참여 현황**이 돌아온다 —
// 화면이 따로 다시 부르지 않게.
////////////////////////////////////////////////////////////////////////////////

/** 내 참여 현황만 다시 읽기 */
export async function fetchEventProgress(
  slug: string,
  userSeq: number
): Promise<DaoApiResponse<EventProgress>> {
  const encrypted = await cipher.encryptJson({ slug, userSeq, action: 'status' });
  return api.post<DaoApiResponse<EventProgress>>('/api/event/entry.json', encrypted);
}

/**
 * 응모 정보 동의 (휴대폰 번호 + 필수 동의 2건).
 *
 * 참여보다 **먼저** 한 번만 한다. 응모권과 따로 남으므로 공유가 실패해도 동의는
 * 남고, 같은 이벤트에서 팝업이 다시 뜨지 않는다.
 */
export async function agreeEvent(
  slug: string,
  userSeq: number,
  mobile: string
): Promise<DaoApiResponse<EventProgress>> {
  const encrypted = await cipher.encryptJson({ slug, userSeq, mobile, action: 'agree' });
  return api.post<DaoApiResponse<EventProgress>>('/api/event/entry.json', encrypted);
}

/**
 * SNS 공유 응모.
 *
 * 실제로 공유했는지는 서버가 알 수 없다 — 카카오도 페이스북도 공유 완료를
 * 알려주지 않는다. 그래서 항목마다 한도가 있다.
 */
export async function submitEventShare(
  slug: string,
  userSeq: number,
  missionSeq: number,
  platform: string,
  mobile?: string
): Promise<DaoApiResponse<EventProgress>> {
  const encrypted = await cipher.encryptJson({
    slug,
    userSeq,
    missionSeq,
    platform,
    mobile,
    action: 'share',
  });
  return api.post<DaoApiResponse<EventProgress>>('/api/event/entry.json', encrypted);
}

/**
 * 카카오 공유 한 건이 어떻게 처리됐는지.
 *
 * `submitEventShare` 가 돌려준 토큰으로 묻는다. 본인 토큰만 결과가 나온다.
 */
export async function fetchShareStatus(
  slug: string,
  userSeq: number,
  token: string
): Promise<DaoApiResponse<EventShareStatus>> {
  const encrypted = await cipher.encryptJson({ slug, userSeq, token, action: 'shareStatus' });
  return api.post<DaoApiResponse<EventShareStatus>>('/api/event/entry.json', encrypted);
}

/** 감상평 등록. 글은 검수를 거쳐야 목록에 보인다. */
export async function submitEventReview(
  slug: string,
  userSeq: number,
  missionSeq: number,
  content: string,
  mobile?: string
): Promise<DaoApiResponse<EventProgress>> {
  const encrypted = await cipher.encryptJson({
    slug,
    userSeq,
    missionSeq,
    content,
    mobile,
    action: 'review',
  });
  return api.post<DaoApiResponse<EventProgress>>('/api/event/entry.json', encrypted);
}

/**
 * 인증샷 업로드.
 *
 * 이것만 multipart 다. 암호화 봉투는 바이트를 정수 배열로 직렬화해서, 사진을
 * 그 안에 담으면 원본의 네 배 가까이 부푼다. 파일은 파일로 보내고 나머지 값만
 * 같은 봉투에 담아 `payload` 필드로 보낸다.
 */
export async function uploadEventShot(
  slug: string,
  userSeq: number,
  missionSeq: number,
  file: File,
  mobile?: string
): Promise<DaoApiResponse<EventProgress>> {
  const encrypted = await cipher.encryptJson({ slug, userSeq, missionSeq, mobile });

  const form = new FormData();
  form.append('payload', JSON.stringify(encrypted));
  form.append('image', file);

  return api.post<DaoApiResponse<EventProgress>>('/api/event/upload.json', form);
}
